/**
 * User Header Component
 *
 * Avatar trigger + compact user menu (#1299, v4 #1377): identity header, two
 * direct navigation rows, three categories - Preferences, Workspace tools,
 * Help - and an isolated destructive Sign out (with a confirm dialog). v4
 * board behavior: on desktop a category expands IN PLACE as an inline
 * accordion (Help's "Available guides" nests a second accordion); on mobile
 * the category opens a side panel to the LEFT. Both are click-driven -
 * hover-to-open is retired. Section mechanics live in ./UserMenu.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconButton,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import {
  IconSun as LightMode,
  IconSparkles as CleaningServices,
  IconCurrencyDollar as AttachMoney,
  IconLogout as Logout,
  IconUsers as People,
  IconUserCog as ManageAccounts,
  IconPhoto as Photo,
  IconHelp as HelpOutline,
  IconCheck,
  IconVolume as VolumeUp,
  IconVolumeOff as VolumeOff,
  IconWorld as Language,
  IconRestore,
  IconBook,
  IconBulb as UiTips,
  IconTools,
  IconAdjustmentsHorizontal,
  IconLifebuoy,
  IconFolderPlus,
  IconFileExport,
  IconArrowsExchange,
  IconShieldLock,
  IconSparkles,
  IconWand
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useUiPrefs } from '../contexts/UiPrefsContext';
import { useDocsDock } from '../contexts/DocsDockContext.jsx';
import { useUserSession } from '../contexts/UserSessionContext.jsx';
import { useGuides } from '../user_guides/GuidesProvider.jsx';
import { getEngineStrings } from '../user_guides/i18n/engineStrings';
import { DEFAULT_LOCALE } from '../user_guides/contract';
import { resetSandbox } from '../user_guides/catalog/catalogClient';
import { useToast } from './ToastNotification';
import { updateProfile } from '../services/profileAPI';
import { useCurrentUser, useSignOut } from '../services/authService';
import { useHoverPanels, InlineCategory, MenuRow, IdentityHeader } from './UserMenu';
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog.jsx';

// Menu labels localize to the selected guide language (#1061) via the
// engine-strings maps; `t` is the flat key -> string map for that locale.
const VOICE_LABEL_KEY = {
  female: 'menu.voiceFemale',
  male: 'menu.voiceMale',
  off: 'menu.voiceOff',
};

function formatExpiryLabel(subscription, t) {
  if (!subscription) return null;
  if (subscription.tier === 'lifetime') return t['menu.lifetimeAccess'];
  if (!subscription.endDate) return null;
  const date = new Date(subscription.endDate);
  if (Number.isNaN(date.getTime())) return null;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${t['menu.expires']}: ${yyyy}-${mm}-${dd}`;
}

const UserHeader = () => {
  const user = useCurrentUser();
  const session = useUserSession();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const guides = useGuides();
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileVersion, setProfileVersion] = useState(
    typeof session?.user?.version === 'number' ? session.user.version : null,
  );
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [createWsOpen, setCreateWsOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { uiTips, toggleUiTips, shimmer, setShimmer } = useUiPrefs();
  // #1979: the latest preferences this menu knows, refreshed from each PUT
  // response. The backend replaces `preferences` wholesale, so every write
  // here sends this object merged with the change.
  const prefsRef = useRef(null);
  // A saved Icon shimmer value wins over the localStorage cache on load.
  const serverShimmer = session?.user?.preferences?.shimmer;
  useEffect(() => {
    if (typeof serverShimmer === 'boolean') setShimmer(serverShimmer);
  }, [serverShimmer, setShimmer]);
  const { showSuccess, showError } = useToast();
  const { openDock: openDocsDock } = useDocsDock();
  const panels = useHoverPanels();
  const muiTheme = useMuiTheme();
  // Design revision (#1388): categories expand in place on BOTH desktop and
  // mobile - the left side panels are retired (they ran off-screen on
  // phones). The breakpoint now only sizes the menu paper (288 xs / 320 sm+).
  const isNarrow = useMediaQuery(muiTheme.breakpoints.down('sm'));
  // Help nests a second in-place accordion for the guide list.
  const [guidesExpanded, setGuidesExpanded] = useState(false);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);

  const closeMenu = () => {
    panels.closeAll();
    setGuidesExpanded(false);
    setLangMenuAnchor(null);
    setShowDropdown(false);
    setAnchorEl(null);
  };

  // Reset practice space (#1021): explicit confirm dialog (a stray click must
  // not wipe the sandbox), then a single POST. The server hard-scopes the wipe
  // to the caller's guideSandbox group, so a real workspace is never at risk.
  const handleResetPick = () => {
    closeMenu();
    setResetConfirmOpen(true);
  };

  // Keep the dialog open with an in-progress signal until the POST resolves
  // (#1066): closing up front left the user with no feedback that the wipe was
  // running or done. Dialog dismissal + both buttons are blocked while busy.
  const handleResetConfirm = () => {
    setResetBusy(true);
    resetSandbox()
      .then(() => showSuccess('Practice space reset.'))
      .catch(() => showError('Could not reset the practice space.'))
      .finally(() => {
        setResetBusy(false);
        setResetConfirmOpen(false);
      });
  };

  // Block backdrop / Escape dismissal mid-reset so the dialog cannot vanish
  // while the POST is still in flight.
  const handleResetDialogClose = () => {
    if (resetBusy) return;
    setResetConfirmOpen(false);
  };

  const handleMembersClick = () => {
    navigate('/members');
    closeMenu();
  };

  const handleSnapshotsClick = () => {
    navigate('/snapshots');
    closeMenu();
  };

  const handleAccountClick = () => {
    navigate('/account');
    closeMenu();
  };

  // Export workspace data (#1871): the controls live on the Account page's
  // Workspaces card (#1786); this row is where a user looks for them first.
  const handleExportClick = () => {
    navigate('/account');
    closeMenu();
  };

  // What's new (#1876): the Feature Spotlight archive of product updates.
  const handleWhatsNewClick = () => {
    navigate('/updates');
    closeMenu();
  };

  const handleAppAdminClick = () => {
    navigate('/admin');
    closeMenu();
  };

  // v4 (#1377 board): sign-out confirms in a proper dialog (the kits carry a
  // sign-out confirmation screen; window.confirm is retired).
  const handleLogout = () => {
    closeMenu();
    setSignOutConfirmOpen(true);
  };

  const handleSignOutConfirm = () => {
    setSignOutConfirmOpen(false);
    signOut();
  };

  const toggleDropdown = (event) => {
    const newShowDropdown = !showDropdown;
    if (!newShowDropdown) {
      closeMenu();
      return;
    }
    setShowDropdown(true);
    setAnchorEl(event.currentTarget);
  };

  // Ledger payees (#1944): link each ledger to the payee its transfers use.
  const handleLedgerPayeesClick = () => {
    navigate('/ledgers/payees');
    closeMenu();
  };

  const handlePayeesCleanupClick = () => {
    navigate('/payees/cleanup');
    closeMenu();
  };

  const handlePaydayClick = () => {
    navigate('/payday-funding');
    closeMenu();
  };

  // Create workspace (#1302): same controlled dialog the Account page mounts;
  // the dialog owns the cap gate and create flow (#753).
  const handleCreateWorkspaceClick = () => {
    closeMenu();
    setCreateWsOpen(true);
  };

  // Documentation (#1064): open the right-side reference panel so the user can
  // read while they keep working. The panel header links out to the full /docs
  // page for a focused read.
  const handleDocsClick = () => {
    closeMenu();
    openDocsDock();
  };

  // Every available guide, newest published first. `version` is `YYYY-MM-DD.NN`
  // (the publish date), so a descending string sort puts the most recently
  // published guide on top. The backend already filters the catalog to
  // `enabled && publishAt <= now`, so no client-side gating is needed. The
  // intro is just one row in this list; restarting it = clicking it.
  const guideList = useMemo(() => {
    const list = Array.isArray(guides.catalog) ? [...guides.catalog] : [];
    return list.sort((a, b) => {
      const va = a.version || '';
      const vb = b.version || '';
      if (va !== vb) return vb.localeCompare(va);
      const oa = a.order || 0;
      const ob = b.order || 0;
      if (oa !== ob) return oa - ob;
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [guides.catalog]);

  const handleGuidePick = (moduleId) => {
    closeMenu();
    guides.start(moduleId);
  };

  // The guide language and Icon shimmer (#1979) persist into the user's
  // `preferences` (closed schema). The UI updates live for the session; the
  // PUT makes it durable. Best-effort - a failed write does not block the UI.
  // (Voice persists in localStorage via the engine's voicePrefs, no profile
  // write.) `patch` is merged over the latest known preferences so a write
  // never drops keys other components own (dismissedTips, updatesSeenAt).
  const persistPreferences = (patch) => {
    if (typeof profileVersion !== 'number') return;
    const preferences = { ...(prefsRef.current ?? session?.user?.preferences ?? {}), ...patch };
    updateProfile({ preferences, version: profileVersion })
      .then((res) => {
        if (res && res.user) {
          setProfileVersion(res.user.version);
          prefsRef.current = res.user.preferences || preferences;
        }
      })
      .catch(() => {});
  };

  const handleShimmerToggle = () => {
    const next = !shimmer;
    setShimmer(next);
    persistPreferences({ shimmer: next });
  };

  // Narration cycle: only the genders the active locale supports, then Off.
  const handleVoiceToggle = () => {
    const cycle = [...(guides.availableVoiceGenders || ['female']), 'off'];
    const idx = cycle.indexOf(guides.voice);
    const next = cycle[(idx + 1) % cycle.length];
    guides.setVoiceGender(next);
  };

  const handleLanguagePick = (code) => {
    closeMenu();
    if (code === guides.language) return;
    guides.setLanguage(code);
    persistPreferences({ language: code });
  };

  // The language picker stays a flyout (it is a picker, not a category);
  // the guide list is a nested in-place accordion everywhere (#1388).
  const openLangPicker = (e) => {
    setLangMenuAnchor(e.currentTarget);
  };
  const closeLangPicker = () => {
    setLangMenuAnchor(null);
  };

  // Deterministic picker order: en-US (authored standard) pinned first, then
  // alphabetical by display label. Independent of languages.json key order.
  const languageEntries = Object.entries(guides.languageConfig || {})
    .sort(([codeA, a], [codeB, b]) => {
      if (codeA === DEFAULT_LOCALE) return -1;
      if (codeB === DEFAULT_LOCALE) return 1;
      return (a.label || codeA).localeCompare(b.label || codeB);
    });
  const currentLanguageLabel =
    (guides.languageConfig && guides.languageConfig[guides.language]
      && guides.languageConfig[guides.language].label) || guides.language;

  // Localized menu labels (#1061): flat map for the selected guide language,
  // en-US filled in underneath so a missing key renders English.
  const t = getEngineStrings(guides.language);

  const CATEGORY_LABEL = {
    workspace: t['menu.workspaceTools'],
    preferences: t['menu.preferences'],
    help: t['menu.help'],
  };

  if (!user) {
    return null;
  }

  const userInitial = (user.email || 'U').charAt(0).toUpperCase();
  const displayName = user.displayName || user.email;
  const voiceLabel = t[VOICE_LABEL_KEY[guides.voice]] || t['menu.voiceFemale'];

  // Guide list rows - shared by the desktop in-place accordion and the
  // mobile flyout. Every guide newest first, then the reset row.
  // Nested-accordion row typography (#1387): ListItemText's inner Typography
  // ignores the section's font-size override, so guide rows rendered at the
  // 16px body default. Pin the accordion scale: 13px primary / 11px dim
  // secondary, matching the sibling Knowledge base row.
  const guideItemTextProps = {
    primaryTypographyProps: { sx: (theme) => ({ fontSize: `${theme.ds.font.meta}px` }) },
    secondaryTypographyProps: { sx: (theme) => ({ fontSize: '11px', color: theme.palette.text.dim }) },
  };

  const renderGuideItems = () => (
    <>
      {guides.catalogLoading ? (
        <MenuItem disabled>
          <ListItemText primary={t['menu.loadingGuides']} {...guideItemTextProps} />
        </MenuItem>
      ) : guideList.length === 0 ? (
        <MenuItem disabled>
          <ListItemText primary={t['menu.noGuides']} {...guideItemTextProps} />
        </MenuItem>
      ) : (
        guideList.map((g) => {
          const done = g.effectivePriority === 'done';
          return (
            <MenuItem
              key={g.moduleId}
              onClick={() => handleGuidePick(g.moduleId)}
            >
              <ListItemText
                primary={g.title}
                secondary={g.estMinutes ? `${g.estMinutes} ${t['menu.minutes']}` : undefined}
                {...guideItemTextProps}
              />
              {done && (
                <IconCheck
                  size={15}
                  style={{ marginLeft: 8, opacity: 0.7, flexShrink: 0 }}
                  aria-label="completed"
                />
              )}
            </MenuItem>
          );
        })
      )}

      <Divider />

      {/* Reset practice space (#1021) - re-walk any guide from a clean
          slate. Disabled mid-guide (the runtime is using the sandbox). */}
      <MenuItem
        onClick={handleResetPick}
        disabled={resetBusy || guides.isRunning}
      >
        <ListItemIcon sx={{ minWidth: '30px' }}>
          <IconRestore size={16} />
        </ListItemIcon>
        <ListItemText
          primary={resetBusy ? t['menu.resetting'] : t['menu.resetPractice']}
          {...guideItemTextProps}
        />
      </MenuItem>
    </>
  );

  // Row order (#1304): alphabetical within each level; Preferences groups the
  // switches above a divider and the value rows below it. Renaming a row means
  // re-sorting it here as well (#1889: the #1715 rename of Payday funding to
  // Budget automation left the row sitting last).
  const panelContent = {
    workspace: (
      <>
        <MenuRow
          dense
          icon={<AttachMoney size={16} />}
          label={t['menu.budgetAutomation']}
          trailing="chevron-right"
          onClick={handlePaydayClick}
        />
        <MenuRow
          dense
          icon={<IconFolderPlus size={16} />}
          label={t['menu.createWorkspace']}
          onClick={handleCreateWorkspaceClick}
        />
        <MenuRow
          dense
          icon={<Photo size={16} style={{ transform: 'rotate(-12deg)' }} />}
          label={t['menu.emailSummaries']}
          trailing="chevron-right"
          onClick={handleSnapshotsClick}
        />
        <MenuRow
          dense
          icon={<IconFileExport size={16} />}
          label={t['menu.exportWorkspaceData']}
          trailing="chevron-right"
          onClick={handleExportClick}
        />
        <MenuRow
          dense
          icon={<IconArrowsExchange size={16} />}
          label={t['menu.ledgerPayees']}
          trailing="chevron-right"
          onClick={handleLedgerPayeesClick}
        />
        <MenuRow
          dense
          icon={<CleaningServices size={16} />}
          label={t['menu.managePayees']}
          trailing="chevron-right"
          onClick={handlePayeesCleanupClick}
        />
      </>
    ),
    preferences: (
      <>
        <MenuRow
          dense
          icon={<UiTips size={16} />}
          label={t['menu.interfaceTips']}
          trailing="switch"
          checked={uiTips}
          onClick={toggleUiTips}
        />
        <MenuRow
          dense
          icon={<LightMode size={16} />}
          label={t['menu.lightMode']}
          trailing="switch"
          checked={!isDarkMode}
          onClick={toggleDarkMode}
        />
        <MenuRow
          dense
          icon={<IconWand size={16} />}
          label={t['menu.shimmer']}
          trailing="switch"
          checked={shimmer}
          onClick={handleShimmerToggle}
        />
        <Divider />
        {languageEntries.length > 0 && (
          <MenuRow
            dense
            icon={<Language size={16} />}
            label={t['menu.language']}
            trailing="value"
            trailingValue={currentLanguageLabel}
            aria-haspopup="menu"
            aria-expanded={Boolean(langMenuAnchor)}
            onClick={openLangPicker}
          />
        )}
        <MenuRow
          dense
          icon={guides.voice === 'off' ? <VolumeOff size={16} /> : <VolumeUp size={16} />}
          label={t['menu.narration']}
          trailing="value"
          trailingValue={voiceLabel}
          onClick={handleVoiceToggle}
        />
      </>
    ),
    help: (
      <>
        {/* #1388: "Available guides" is a nested in-place accordion on every
            viewport - the flyout picker is retired with the side panels. */}
        <MenuRow
          dense
          icon={<HelpOutline size={16} />}
          label={t['menu.availableGuides']}
          trailing="expand"
          expanded={guidesExpanded}
          aria-expanded={guidesExpanded}
          onClick={() => setGuidesExpanded((v) => !v)}
        />
        <InlineCategory
          component="div"
          id="user-menu-guides-inline"
          open={guidesExpanded}
          label={t['menu.availableGuides']}
          depth={1}
        >
          {renderGuideItems()}
        </InlineCategory>
        <MenuRow
          dense
          icon={<IconBook size={16} />}
          label={t['menu.knowledgeBase']}
          trailing="chevron-right"
          onClick={handleDocsClick}
        />
        <MenuRow
          dense
          icon={<IconSparkles size={16} />}
          label={t['menu.whatsNew']}
          trailing="chevron-right"
          onClick={handleWhatsNewClick}
        />
      </>
    ),
  };

  return (
    <>
      <IconButton
        onClick={toggleDropdown}
        aria-expanded={showDropdown}
        aria-label="User menu"
        data-tour-id="user-avatar"
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px',
          padding: '8px 12px',
          '&:hover': {
            bgcolor: theme.palette.action.hover,
          },
        })}
      >
        <Avatar
          sx={(theme) => ({
            // v4 board avatar (#1377): 24px, tokenized gradient (was a
            // hardcoded #667eea/#764ba2 pair), 11px medium initial.
            width: theme.ds.size.avatarBar,
            height: theme.ds.size.avatarBar,
            background: `linear-gradient(135deg, ${theme.palette.interactiveStrong}, ${theme.palette.accentDeep})`,
            fontSize: '11px',
            fontWeight: theme.ds.font.weightMedium,
            color: theme.palette.interactiveOn,
          })}
        >
          {userInitial}
        </Avatar>
        <Typography
          sx={{
            ml: 1,
            mr: 0.5,
            fontWeight: 500,
            fontSize: '14px',
            color: 'bar.text',
            maxWidth: { xs: '80px', sm: '150px' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            // v4 board: name shows on wide desktop only (mobile = avatar + caret).
            display: { xs: 'none', md: 'block' },
          }}
        >
          {displayName}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '10px',
            color: 'bar.label',
            transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          ▼
        </Typography>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={showDropdown}
        onClose={closeMenu}
        disableEnforceFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={(theme) => ({ zIndex: theme.zIndex.modal + 200 })}
        MenuListProps={{
          onKeyDown: (e) => {
            // First Escape closes an open category panel; the second reaches
            // the Modal and closes the menu itself.
            if (e.key === 'Escape' && panels.openPanel) {
              e.preventDefault();
              e.stopPropagation();
              panels.closeAll();
            }
          },
        }}
        PaperProps={{
          sx: (theme) => ({
            mt: 1,
            // v4 board (#1377): 320w desktop / 288w mobile; the overlay
            // surface, hairline, and shadow come from the global MuiMenu
            // override (section 2.7).
            width: isNarrow ? 288 : 320,
            borderRadius: `${theme.ds.radius.overlay}px`,
          })
        }}
      >
        <IdentityHeader
          name={user.displayName}
          email={user.email}
          initial={userInitial}
        />

        <Divider />

        {/* Direct navigation rows */}
        <MenuRow
          icon={<ManageAccounts size={18} />}
          label={t['menu.account']}
          trailing="chevron-right"
          onClick={handleAccountClick}
        />
        <MenuRow
          icon={<People size={18} />}
          label={t['menu.members']}
          trailing="chevron-right"
          onClick={handleMembersClick}
        />

        {/* Category rows (#1304: alphabetical; Help sits alone below its own
            divider). #1388: click expands in place on EVERY viewport - the
            menu never grows sideways, so it cannot run off-screen. */}
        <MenuRow
          icon={<IconAdjustmentsHorizontal size={18} />}
          label={CATEGORY_LABEL.preferences}
          trailing="expand"
          expanded={panels.openPanel === 'preferences'}
          {...panels.getCategoryRowProps('preferences')}
        />
        <InlineCategory
          id="user-menu-panel-preferences"
          open={panels.openPanel === 'preferences'}
          label={CATEGORY_LABEL.preferences}
        >
          {panelContent.preferences}
        </InlineCategory>
        <MenuRow
          icon={<IconTools size={18} />}
          label={CATEGORY_LABEL.workspace}
          trailing="expand"
          expanded={panels.openPanel === 'workspace'}
          {...panels.getCategoryRowProps('workspace')}
        />
        <InlineCategory
          id="user-menu-panel-workspace"
          open={panels.openPanel === 'workspace'}
          label={CATEGORY_LABEL.workspace}
        >
          {panelContent.workspace}
        </InlineCategory>

        <Divider />

        <MenuRow
          icon={<IconLifebuoy size={18} />}
          label={CATEGORY_LABEL.help}
          trailing="expand"
          expanded={panels.openPanel === 'help'}
          {...panels.getCategoryRowProps('help')}
        />
        <InlineCategory
          id="user-menu-panel-help"
          open={panels.openPanel === 'help'}
          label={CATEGORY_LABEL.help}
        >
          {panelContent.help}
        </InlineCategory>

        <Divider />

        {/* Sign out - isolated, destructive */}
        <MenuRow
          icon={<Logout size={18} />}
          label={t['menu.signOut']}
          destructive
          onClick={handleLogout}
        />

        {(() => {
          const expiryLabel = formatExpiryLabel(session?.subscription, t);
          if (!expiryLabel) return null;
          // v4 board footer: quiet 12px dim access line under a hairline.
          return (
            <li role="presentation">
              <Box
                aria-label="subscription-expiry"
                sx={(theme) => ({
                  borderTop: `1px solid ${theme.palette.divider}`,
                  marginTop: '4px',
                  padding: '10px 16px 6px',
                  fontSize: '12px',
                  color: theme.palette.text.dim,
                })}
              >
                {expiryLabel}
              </Box>
            </li>
          );
        })()}

        {/* #1427 convention 13: the Application admin row is visible only to
            application admins, BELOW the access-level footer, isolated below
            a divider like Sign out. */}
        {session?.isAppAdmin && <Divider />}
        {session?.isAppAdmin && (
          <MenuRow
            icon={<IconShieldLock size={18} />}
            label={t['menu.appAdmin']}
            trailing="chevron-right"
            onClick={handleAppAdminClick}
          />
        )}
      </Menu>

      {/* Sign out confirm (v4 #1377 board): outlined destructive action,
          never a filled red button (section 8.5). */}
      <Dialog
        open={signOutConfirmOpen}
        onClose={() => setSignOutConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="sign-out-title"
      >
        <DialogTitle id="sign-out-title">Sign out?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to sign out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignOutConfirmOpen(false)}>Cancel</Button>
          <Button variant="outlined" color="error" onClick={handleSignOutConfirm}>
            Sign out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset practice space confirm (#1021) - explicit, no easy-key wipe */}
      <Dialog
        open={resetConfirmOpen}
        onClose={handleResetDialogClose}
        aria-labelledby="reset-sandbox-title"
      >
        <DialogTitle id="reset-sandbox-title">Reset practice space?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This empties every ledger, budget, and transaction in your guide
            practice space, so you can re-walk a guide from a clean slate.
            Your real workspaces are not touched.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetDialogClose} disabled={resetBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleResetConfirm}
            variant="contained"
            disableElevation
            disabled={resetBusy}
            startIcon={resetBusy
              ? <CircularProgress size={16} color="inherit" aria-hidden />
              : undefined}
          >
            {resetBusy ? t['menu.resetting'] : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create workspace (#1302) - shared dialog, same as Account page */}
      <CreateWorkspaceDialog
        open={createWsOpen}
        onClose={() => setCreateWsOpen(false)}
      />

      {/* Language picker - every published locale from the guide config */}
      <Menu
        anchorEl={langMenuAnchor}
        open={Boolean(langMenuAnchor)}
        onClose={closeLangPicker}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={(theme) => ({ zIndex: theme.zIndex.modal + 300 })}
        PaperProps={{ sx: { maxHeight: 320 } }}
      >
        {languageEntries.map(([code, entry]) => (
          <MenuItem
            key={code}
            selected={code === guides.language}
            onClick={() => handleLanguagePick(code)}
          >
            <ListItemText primary={entry.label || code} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default UserHeader;
